import React, {Suspense} from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import {createTheme, CssBaseline, ThemeProvider} from "@mui/material";
import {Provider} from "react-redux";
import {appStore, storePersistor} from "./store/AppStore";
import {createHashRouter, RouterProvider} from "react-router-dom";
import {AppRoute} from "./book/AppRoute";
import {Login} from "./component/page/login/Login";
import {ProductSearch} from "./component/page/product-search/ProductSearch";
import i18n from "./book/i18n";
import {I18nextProvider} from "react-i18next";
import {PersistGate} from "redux-persist/integration/react";
import {Loader} from "./component/common/loader/Loader";
import {UsersManagement} from "./component/page/user-management/UsersManagement";
import {Credits} from "./component/page/credits/Credits";
import {ProductDetails} from "./component/page/product-details/ProductDetails";
import {Configuration} from "./component/page/configuration/Configuration";
import {MetadataManagement} from "./component/page/metadata-management/MetadataManagement";
import {Profile} from "./component/page/profile/Profile";
import * as Sentry from "@sentry/react";

const theme = createTheme({
    palette: {
        primary: {
            main: '#FE0000'
        },
        secondary: {
            main: '#FE0000'
        },
    },
});

const router = createHashRouter([
    {
        path: AppRoute.Login,
        element: <Login/>,
    },
    {
        path: AppRoute.ProductsSearch,
        element: <ProductSearch/>,
    },
    {
        path: AppRoute.UsersManagement,
        element: <UsersManagement/>,
    },
    {
        path: AppRoute.Credits,
        element: <Credits/>,
    },
    {
        path: AppRoute.ProductDetails,
        element: <ProductDetails/>,
    },
    {
        path: AppRoute.AppConfigurations,
        element: <Configuration/>,
    },
    {
        path: AppRoute.MetadataManagement,
        element: <MetadataManagement/>,
    },
    {
        path: AppRoute.Profile,
        element: <Profile/>,
    },
]);

const sentryDsn: string = import.meta.env.VITE_SENTRY_DSN as string;
const sentryEnvironment: string = import.meta.env.VITE_ENVIRONMENT as string;
if (sentryDsn && sentryEnvironment) {
    Sentry.init({
        dsn: sentryDsn,
        debug: false,
        environment: sentryEnvironment,
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration(),
        ],
        // Tracing
        tracesSampleRate: 1.0, //  Capture 100% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: [
            "localhost",
            /^https:\/\/gtfrontenddev.z6.web.core.windows.net/,
            /^https:\/\/governancetool-qa.corp.carel.com/,
            /^https:\/\/governancetool.corp.carel.com/,
        ],
        // Session Replay
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
}

const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
);
root.render(<React.StrictMode>
    <ThemeProvider theme={theme}>
        <Provider store={appStore}>
            <Suspense fallback={<Loader/>}>
                <I18nextProvider i18n={i18n}>
                    <PersistGate loading={null} persistor={storePersistor}>
                        <CssBaseline/>
                        <RouterProvider router={router}/>
                    </PersistGate>
                </I18nextProvider>
            </Suspense>
        </Provider>
    </ThemeProvider>
</React.StrictMode>);